if (!customElements.get('m-image-comparison')) {
  class ImageComparison extends HTMLElement {
    constructor() {
      super();
      this.active = false;
      this.userInteracted = false;
      this.demoStarted = false;
      this.demoTimers = [];
      this.button = this.querySelector('.m-image-comparison__button');
      this.horizontal = this.dataset.layout === 'horizontal';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.init();
    }

    init() {
      if (!this.button) return;

      this.setAttribute('is-visible', '');
      this.setPercent(50);
      this.button.addEventListener('pointerdown', this.start.bind(this));
      window.addEventListener('pointerup', this.end.bind(this));
      window.addEventListener('pointercancel', this.end.bind(this));
      window.addEventListener('pointermove', this.move.bind(this), { passive: false });
      this.button.addEventListener('keydown', this.keydown.bind(this));

      if (this.hasAttribute('data-auto-demo') && !this.prefersReducedMotion) {
        this.observe();
      }
    }

    observe() {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.playDemo();
      }, { threshold: 0.45 });

      observer.observe(this);
    }

    playDemo() {
      if (this.demoStarted || this.userInteracted) return;

      this.demoStarted = true;
      this.classList.add('m-is-demoing');
      this.setPercent(98);
      this.demoTimers.push(window.setTimeout(() => this.setPercent(2), 1200));
      this.demoTimers.push(window.setTimeout(() => this.setPercent(50), 2450));
      this.demoTimers.push(window.setTimeout(() => this.classList.remove('m-is-demoing'), 3550));
    }

    cancelDemo() {
      this.demoTimers.forEach((timer) => window.clearTimeout(timer));
      this.demoTimers = [];
      this.classList.remove('m-is-demoing');
    }

    markInteracted() {
      if (this.userInteracted) return;

      this.userInteracted = true;
      this.cancelDemo();
    }

    start(event) {
      event.preventDefault();
      this.markInteracted();
      this.active = true;
      this.classList.add('image-comparing');
      document.documentElement.classList.add('prevent-scroll');
      this.updateFromPointer(event);
    }

    end() {
      if (!this.active) return;

      this.active = false;
      this.classList.remove('image-comparing');
      document.documentElement.classList.remove('prevent-scroll');
      document.body.style.removeProperty('padding-right');
    }

    move(event) {
      if (!this.active) return;

      event.preventDefault();
      this.updateFromPointer(event);
    }

    updateFromPointer(event) {
      const rect = this.getBoundingClientRect();
      const position = this.horizontal ? event.clientX - rect.left : event.clientY - rect.top;
      const size = this.horizontal ? rect.width : rect.height;
      const percent = 100 * Math.max(20, Math.min(position, size - 20)) / size;
      this.setPercent(percent);
    }

    keydown(event) {
      const decreaseKeys = this.horizontal ? ['ArrowLeft'] : ['ArrowUp'];
      const increaseKeys = this.horizontal ? ['ArrowRight'] : ['ArrowDown'];

      if (!decreaseKeys.includes(event.key) && !increaseKeys.includes(event.key)) return;

      event.preventDefault();
      this.markInteracted();
      const current = Number.parseFloat(this.style.getPropertyValue('--percent')) || 50;
      this.setPercent(current + (increaseKeys.includes(event.key) ? 5 : -5));
    }

    setPercent(value) {
      const percent = Math.max(2, Math.min(value, 98));
      this.style.setProperty('--percent', percent + '%');
      this.button.setAttribute('aria-valuenow', String(Math.round(percent)));
    }
  }

  customElements.define('m-image-comparison', ImageComparison);
}
